import { MEASUREMENT_TABLE_PURPOSES } from './printDesarrolloLayout';

const SURVEY_EXAMPLE_LABEL_PATTERN =
  /manzana|pl[aá]tano|uva|naranja|pera|fruta|frutas?\s+favorit|f[uú]tbol|futbol|b[aá]squet|basquet|tenis|mascota|videojuego|pel[ií]cula|deportes?\s+favorit/i;

const INTERNAL_GUIDE_LABEL_PATTERN =
  /\bgu[ií]a de\b|interpretaci[oó]n de datos|construcci[oó]n de un gr[aá]fico|gr[aá]fico a partir de una encuesta|data_statistics/i;

export const SURVEY_PURPOSE_TITLES: Record<string, string> = {
  survey_design: 'Diseña tu pregunta',
  survey_collect: 'Registra las respuestas',
  survey_chart: 'Construye tu gráfico',
  survey_interpret: 'Interpreta los resultados',
  survey_reflect: 'Reflexión sobre la encuesta',
  survey_frequency_calc: 'Ordena los datos',
  survey_compare: 'Compara los resultados',
};

export function isInternalGuideLabel(text: string): boolean {
  const value = String(text || '').trim();
  if (!value) return true;
  if (INTERNAL_GUIDE_LABEL_PATTERN.test(value.toLowerCase())) return true;
  const letters = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ]/g, '');
  return (
    letters.length > 18 &&
    value === value.toUpperCase() &&
    /GUIA|ENCUESTA|GRAFICO|GRÁFICO|DATOS|CONSTRU|INTERPRET/i.test(value)
  );
}

const INTERNAL_SLOT_LABEL_PATTERN =
  /\bslot\b|blueprint|data_statistics|survey_(design|collect|chart|interpret|frequency_calc|compare|reflect|sample_limits)/i;

export function isUnfitSurveyStudentLabel(text: string): boolean {
  const value = String(text || '').trim();
  if (!value) return true;
  if (isSurveyExampleLabel(value) || isInternalGuideLabel(value)) return true;
  if (INTERNAL_SLOT_LABEL_PATTERN.test(value)) return true;
  if (/crea+\s+tu.*opci|pregunta\s+y\s+opci|creaa|opici[oó]n/i.test(value)) return true;
  return false;
}

export function surveyStudentTitleForPurpose(purpose: string, mode = ''): string {
  const key = String(purpose || '').trim();
  if (SURVEY_PURPOSE_TITLES[key]) return SURVEY_PURPOSE_TITLES[key];
  if (mode === 'data_chart') return 'Construye tu gráfico';
  if (mode === 'completar') return 'Registra las respuestas';
  if (mode === 'desarrollo') return 'Responde y explica';
  return '';
}

export function stripPracticePrefix(raw: string): string {
  return String(raw || '')
    .replace(/^pr[aá]ctica\s*:\s*/i, '')
    .trim();
}

export function resolveSurveyChartPrintMeta(payload: Record<string, unknown>): {
  title: string;
  xAxisLabel: string;
  yAxisLabel: string;
} {
  const clean = (value: unknown, fallback: string) => {
    const text = String(value || '').trim();
    return isUnfitSurveyStudentLabel(text) ? fallback : text;
  };

  return {
    title: clean(payload.title, 'Gráfico de barras — resultados de tu encuesta'),
    xAxisLabel: clean(payload.xAxisLabel, 'Opciones de respuesta'),
    yAxisLabel: clean(payload.yAxisLabel, 'Cantidad de votos'),
  };
}

export function resolveSurveyBlockTitle(
  block: { mode?: string; label?: string; payload?: Record<string, unknown> },
): string {
  const payload = (block.payload ?? {}) as Record<string, unknown>;
  const purpose = String((block as { _blueprintPurpose?: string })._blueprintPurpose || '');
  const mode = String(block.mode || '');
  const isSurveyBlock =
    purpose.startsWith('survey_') ||
    payload.surveyTable != null ||
    payload.surveyChartTemplate === true ||
    payload.surveyResponseTemplate != null ||
    mode === 'data_chart';

  if (!isSurveyBlock) return '';

  const fallback = surveyStudentTitleForPurpose(purpose, mode);

  const candidates = [
    block.label,
    payload.title,
    payload.instruction,
    payload.prompt,
  ]
    .map((value) => stripPracticePrefix(String(value || '')))
    .filter(Boolean);

  for (const candidate of candidates) {
    if (!isUnfitSurveyStudentLabel(candidate)) return candidate;
  }

  return fallback;
}

export function isSurveyDesarrolloBlock(
  block: {
    mode?: string;
    label?: string;
    _blueprintPurpose?: string;
    payload?: Record<string, unknown>;
  },
  prompt = '',
): boolean {
  if (String(block.mode || '') !== 'desarrollo') return false;

  const purpose = String(block._blueprintPurpose || '');
  if (purpose.startsWith('survey_')) return true;

  const payload = (block.payload ?? {}) as Record<string, unknown>;
  if (payload.surveyResponseTemplate) return true;

  const labelBlob = [block.label, payload.title].map(String).join(' ').toLowerCase();
  if (/interpretaci[oó]n|frecuencia|comparaci[oó]n|reflexi|dise[ñn]o.*pregunta|muestra|limitaci/.test(labelBlob)) {
    return true;
  }
  if (/survey_(design|collect|chart|interpret|frequency_calc|compare|reflect|sample_limits)/.test(labelBlob)) {
    return true;
  }

  const blob = `${prompt} ${labelBlob}`.toLowerCase();
  return /encuesta|gr[aá]fico|votos|frecuencia|tabla/.test(blob);
}

export function shouldEmbedSurveyStimulusInBody(block: {
  mode?: string;
  label?: string;
  _blueprintPurpose?: string;
  payload?: Record<string, unknown>;
}): boolean {
  const purpose = String(block._blueprintPurpose || '');
  const payload = (block.payload ?? {}) as Record<string, unknown>;
  const mode = String(block.mode || '');

  if (purpose.startsWith('survey_') && (mode === 'desarrollo' || mode === 'completar')) {
    return true;
  }

  if (mode === 'completar' && isSurveyCompletarPayload(payload)) return true;

  if (mode === 'desarrollo') {
    const prompt = String(payload.prompt || payload.title || '');
    if (payload.surveyResponseTemplate) return true;
    if (isSurveyDesarrolloBlock(block, prompt)) return true;
  }

  return false;
}

export function sanitizePrintStimulus(
  stimulus: { title?: string; content?: string; theme?: string; type?: string; imageUrl?: string } | undefined,
  payload: Record<string, unknown> = {},
): typeof stimulus {
  if (!stimulus?.content?.trim()) return stimulus;

  const next = { ...stimulus };
  const title = String(next.title || '').trim();

  if (isUnfitSurveyStudentLabel(title)) {
    delete next.title;
  }

  const isSurveyPayload =
    payload.surveyResponseTemplate != null ||
    payload.surveyTable != null ||
    payload.surveyChartTemplate === true ||
    payload.surveyTableMode != null;

  if (isSurveyPayload) {
    next.theme = 'prose';
    next.type = 'prose';
  }

  return next;
}

export function resolveSanitizedPrintStimulus(
  payload: Record<string, unknown>,
  mode: string,
  resolve: (payload: Record<string, unknown>, mode: string) => ReturnType<typeof sanitizePrintStimulus>,
): ReturnType<typeof sanitizePrintStimulus> {
  return sanitizePrintStimulus(resolve(payload, mode), payload);
}

export function normalizeSurveyInstruction(
  prompt: string,
  stimulusContent?: string,
): string {
  const value = String(prompt || '').trim();
  if (!value || !stimulusContent) return value;

  const stimulusNorm = stimulusContent.replace(/\s+/g, ' ').trim().slice(0, 80);
  const promptNorm = value.replace(/\s+/g, ' ').trim().slice(0, 80);
  if (stimulusNorm && promptNorm.startsWith(stimulusNorm.slice(0, 40))) return '';

  return value;
}

const MUSIC_CONTAMINATION_PATTERN =
  /comp[aá]s(es)?\s+regular|figuras?\s+r[ií]tmic|takadimi|pentagrama|notaci[oó]n\s+musical|corchea|semicorchea|redonda|blanca|negra|ta-ka-di-mi|♩|♫|♪/i;

export function isSurveyExampleLabel(text: string): boolean {
  return SURVEY_EXAMPLE_LABEL_PATTERN.test(text);
}

export function isMusicContaminatedSurveyText(text: string): boolean {
  return MUSIC_CONTAMINATION_PATTERN.test(text);
}

export function isSurveyCompletarPayload(payload: Record<string, unknown>): boolean {
  if (payload.surveyTable && typeof payload.surveyTable === 'object') return true;

  const title = String(payload.title || '').toLowerCase();
  const text = String(payload.text || '').toLowerCase();
  const blob = `${title} ${text}`;

  if (/encuesta|tabla|registro|votos?|frecuencia/.test(blob)) {
    if (/\|\s*n/i.test(text) || /respuesta/.test(blob) || /tabla/.test(blob)) return true;
  }

  return false;
}

export function isInventedSurveyChartPayload(payload: Record<string, unknown>): boolean {
  const data = Array.isArray(payload.data) ? payload.data : [];
  if (!data.length) return false;

  if (data.some((point) => Number((point as { value?: unknown }).value) > 0)) return true;

  const title = String(payload.title || '').toLowerCase();
  const axes = `${payload.xAxisLabel || ''} ${payload.yAxisLabel || ''}`.toLowerCase();
  if (/frutas?\s+favorit|deportes?\s+favorit|ejemplo\s+de\s+resultado/.test(`${title} ${axes}`)) return true;

  const labels = data.map((point) => String((point as { label?: unknown }).label || '')).join(' ');
  if (isSurveyExampleLabel(labels) && data.length >= 2) return true;

  const letterLabels = data
    .map((point) => String((point as { label?: unknown }).label || '').trim())
    .filter(Boolean);
  if (
    letterLabels.length >= 3 &&
    letterLabels.every((label) => /^[A-D]$/i.test(label))
  ) {
    return true;
  }

  return false;
}

export function isSurveyDataChartPayload(
  payload: Record<string, unknown>,
  purpose = '',
): boolean {
  if (MEASUREMENT_TABLE_PURPOSES.has(purpose)) return false;
  if (purpose === 'survey_chart' || payload.surveyChartTemplate === true) return true;

  const blob = [
    payload.title,
    payload.instruction,
    payload.prompt,
    payload.xAxisLabel,
    payload.yAxisLabel,
  ]
    .map(String)
    .join(' ')
    .toLowerCase();

  return /encuesta|gr[aá]fico|votos|frecuencia|resultados|barras/.test(blob);
}

export function cleanSurveyChartInstruction(
  payload: Record<string, unknown>,
  purpose = '',
): string | undefined {
  for (const raw of [payload.instruction, payload.prompt, payload.text]) {
    const cleaned = String(raw || '')
      .replace(/\|[^|]+\|/g, '')
      .replace(/\[[^\]]+\]/g, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
    if (
      cleaned &&
      !isUnfitSurveyStudentLabel(cleaned) &&
      !/\|\s*[^|]+\s*\|/.test(String(raw || '')) &&
      !/\[[^\]]+\]/.test(String(raw || ''))
    ) {
      return cleaned;
    }
  }

  if (purpose === 'survey_chart') {
    return 'Usa los datos de tu tabla para construir un gráfico de barras con los resultados de tu encuesta.';
  }

  return undefined;
}

const SURVEY_CHART_HINTS: Record<string, string> = {
  survey_chart:
    'Usa los datos de tu tabla. Escribe el nombre de cada opción debajo y pinta la altura de cada barra según la cantidad de votos.',
  default:
    'Escribe el nombre de cada opción debajo y pinta la altura de cada barra según la cantidad de votos.',
};

export function resolveSurveyChartPrintConfig(
  payload: Record<string, unknown>,
  purpose = '',
  options: { isLearningGuide?: boolean } = {},
): {
  useTemplate: boolean;
  instruction?: string;
  meta: ReturnType<typeof resolveSurveyChartPrintMeta>;
  columnCount: number;
  hint: string;
} {
  const data = Array.isArray(payload.data) ? payload.data : [];
  const optionCount = Math.min(
    Math.max(Number(payload.surveyOptionCount) || data.length || 4, 3),
    6,
  );

  return {
    useTemplate: shouldRenderSurveyChartTemplate(payload, options, purpose),
    instruction: cleanSurveyChartInstruction(payload, purpose),
    meta: resolveSurveyChartPrintMeta(payload),
    columnCount: optionCount,
    hint: SURVEY_CHART_HINTS[purpose] || SURVEY_CHART_HINTS.default,
  };
}

export function shouldRenderSurveyChartTemplate(
  payload: Record<string, unknown>,
  options: { isLearningGuide?: boolean } = {},
  purpose = '',
): boolean {
  if (MEASUREMENT_TABLE_PURPOSES.has(purpose)) return false;
  if (payload.surveyChartTemplate === true) return true;
  if (purpose === 'survey_chart') return true;
  if (!options.isLearningGuide) return false;
  if (isInventedSurveyChartPayload(payload)) return true;
  if (!isSurveyDataChartPayload(payload, purpose)) return false;

  const data = Array.isArray(payload.data) ? payload.data : [];
  if (!data.length) return true;

  return data.every((point) => {
    const label = String((point as { label?: unknown }).label || '').trim();
    const value = Number((point as { value?: unknown }).value);
    const genericLabel = !label || /^opci[oó]n\s+\d+$/i.test(label);
    return genericLabel && value === 0;
  });
}

export function resolveSurveyTableConfig(
  payload: Record<string, unknown>,
  purpose = '',
): {
  columns: string[];
  rows: number;
  mode: 'simple' | 'multi_option';
} {
  const raw = payload.surveyTable as
    | { columns?: string[]; rows?: number; mode?: string }
    | undefined;

  if (raw && Array.isArray(raw.columns) && raw.columns.length >= 2) {
    const mode = raw.mode === 'multi_option' || raw.columns.length > 2 ? 'multi_option' : 'simple';
    return {
      columns: raw.columns.map(String),
      rows: Math.min(Math.max(Number(raw.rows) || 8, 4), 20),
      mode,
    };
  }

  const optionCount = Math.min(Math.max(Number(payload.surveyOptionCount) || 4, 3), 6);
  const isCollect =
    purpose === 'survey_collect' ||
    payload.surveyTableMode === 'multi_option' ||
    /opci[oó]n\s*1|marca.*x|nombre del compa/i.test(String(payload.text || ''));

  if (isCollect) {
    return {
      columns: [
        'N°',
        'Nombre',
        ...Array.from({ length: optionCount }, (_, index) => `Opción ${index + 1}`),
      ],
      rows: 12,
      mode: 'multi_option',
    };
  }

  return { columns: ['N°', 'Respuesta'], rows: 8, mode: 'simple' };
}
